import {
  useId,
  type ChangeEvent,
  type CSSProperties,
  type InputHTMLAttributes
} from "react";

const visuallyHiddenInputStyle: CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0
};

export type UseRadioProps = {
  value?: string | number;
  disabled?: boolean;
  isChecked?: boolean;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  [key: string]: unknown;
};

export function useRadio(props: UseRadioProps = {}) {
  const { value, disabled, isChecked, name, onChange, ...rest } = props;
  const checked = Boolean(isChecked);

  const getInputProps = (
    inputProps: InputHTMLAttributes<HTMLInputElement> &
      Record<string, unknown> = {}
  ) => {
    const { style, ...restInputProps } = inputProps;

    return {
      type: "radio" as const,
      name,
      value,
      checked,
      disabled: disabled,
      onChange,
      ...restInputProps,
      style: {
        ...visuallyHiddenInputStyle,
        ...(style as CSSProperties | undefined)
      }
    };
  };

  const getRadioProps = (radioProps: Record<string, unknown> = {}) => ({
    "data-checked": checked ? "" : undefined,
    "data-disabled": disabled ? "" : undefined,
    "aria-checked": checked,
    ...rest,
    ...radioProps
  });

  return {
    getInputProps,
    getRadioProps,
    state: { isChecked: checked, disabled: Boolean(disabled) }
  };
}

export function useRadioGroup(props: {
  value?: string;
  defaultValue?: string;
  onChange?: (nextValue: string) => void;
  name?: string;
  disabled?: boolean;
}) {
  const generatedName = useId();
  const name = props.name ?? generatedName;

  const getRootProps = (rootProps: Record<string, unknown> = {}) => ({
    role: "radiogroup",
    ...rootProps
  });

  const getRadioProps = (radioProps: UseRadioProps = {}) => {
    const radioValue = radioProps.value != null ? String(radioProps.value) : "";
    const groupValue = props.value != null ? String(props.value) : "";
    const isChecked = groupValue === radioValue;
    const disabled = Boolean(props.disabled || radioProps.disabled);

    return {
      ...radioProps,
      name,
      value: radioValue,
      isChecked,
      disabled,
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        props.onChange?.(event.target.value);
        radioProps.onChange?.(event);
      }
    };
  };

  return { getRootProps, getRadioProps };
}
